<script lang="ts" setup>
import { ref, onMounted, onUnmounted, useSlots, nextTick } from 'vue';
import { useWindowScroll, watchThrottled } from '@vueuse/core';
import scrollama from 'scrollama';

const contentEl = ref();
const lineEl = ref();
const navigatorEl = ref<HTMLElement>();
const fixed = ref(false);

const scroller = scrollama();
const slots = useSlots();

const tabLabels = ref(slots.default?.().map(tab => tab.props?.label));

const props = defineProps({
  barClass: {
    type: String,
    default: '',
  },
});

const goto = (index: number) => {
  if (!contentEl.value.children[index]) {
    return;
  }

  nextTick(() => {
    window.scrollTo({
      top: window.scrollY + contentEl.value.children[index].getBoundingClientRect().top - 100,
      left: 0,
      behavior: 'smooth',
    });
  });
}

const { y } = useWindowScroll();

onMounted(() => {
  lineEl.value.style.width = `${100 / contentEl.value.children.length}%`;
  lineEl.value.style.opacity = '1';
  lineEl.value.style.height = '2px';

  const offsetTop = navigatorEl.value?.offsetTop ?? 0;

  watchThrottled(y, (value) => {
    fixed.value = value > offsetTop;
  }, {
    throttle: 100,
  });

  scroller.setup({
    step: Array.from(contentEl.value.children),
    offset: 0.3,
  }).onStepEnter(({ index }) => {
    lineEl.value.style.transform = `translateX(${index * 100}%)`;
  });
});

onUnmounted(() => {
  scroller.destroy();
});

defineExpose({
  goto,
});
</script>

<template>
  <div class="float-navigator">
    <div class="float-navigator__bar z-60" :class="[ fixed ? 'fixed' : 'relative', props.barClass ]" ref="navigatorEl">
      <ul class="m-0 h-full flex list-none justify-between p-0">
        <li v-for="(item, index) in tabLabels" :key="index" class="h-full flex flex-grow basis-0 cursor-pointer items-center justify-center text-center" @click="goto(index)">
          {{ item }}
        </li>
      </ul>
      <div class="float-navigator__line absolute bottom-0 left-0 transition-all duration-300" ref="lineEl"></div>
    </div>

    <div class="float-navigator__content" ref="contentEl">
      <slot></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.float-navigator {
  $navigator-height: 64px;

  .float-navigator__bar {
    top: 0;
    right: 0;
    left: 0;
    height: $navigator-height;
    margin-top: calc(-1 * $navigator-height / 2);
    background-color: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    transition: width, max-width .2s;

    &.fixed {
      width: 100%;
      max-width: 100%;
      margin-top: 0;
    }

    li {
      font-size: 16px;

      &:hover {
        color: #1180ff;
      }
    }

    .float-navigator__line {
      height: 0;
      background-color: #1180ff;
      opacity: 0;
    }
  }

  .float-navigator__content {
    margin-top: calc(-1 * $navigator-height / 2);
  }
}
</style>
